import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, User, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/about", label: "About Theo", testid: "nav-about-link" },
  { to: "/packages", label: "Packages", testid: "nav-packages-link" },
  { to: "/schedule", label: "Schedule", testid: "nav-schedule-link" },
  { to: "/plans", label: "Training Plans", testid: "nav-plans-link" },
  { to: "/summer-camp", label: "Summer Camp", testid: "nav-camp-link" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, openAuthModal, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#090A0C]/85 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-full bg-court-amber flex items-center justify-center font-display font-black text-court-void text-lg leading-none group-hover:rotate-12 transition-transform duration-300">T</div>
          <div className="font-display font-extrabold uppercase tracking-tight text-lg leading-none">
            Theo <span className="text-court-amber">Basketball Lab</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.testid}
              className={({ isActive }) => `font-mono text-xs uppercase tracking-widest transition-colors hover:text-court-amber ${isActive ? "text-court-amber" : "text-zinc-400"}`}>
              {l.label}
            </NavLink>
          ))}
          {user && user.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin-link"
              className={({ isActive }) => `font-mono text-xs uppercase tracking-widest transition-colors hover:text-court-amber ${isActive ? "text-court-amber" : "text-zinc-400"}`}>
              Admin
            </NavLink>
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <span data-testid="nav-user-name" className="flex items-center gap-2 text-sm text-zinc-300"><User size={15} className="text-court-amber" />{user.name}</span>
              <button data-testid="nav-logout-btn" onClick={handleLogout} className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-red-400 transition-colors">
                <LogOut size={14} /> Logout
              </button>
            </>
          ) : (
            <button data-testid="nav-login-btn" onClick={() => openAuthModal()} className="btn-amber bg-court-amber text-court-void font-display font-bold uppercase tracking-wide px-5 py-2 rounded-lg text-sm">
              Member Login
            </button>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className="lg:hidden text-zinc-300" onClick={() => setOpen(!open)}>
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-[#090A0C]/95">
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={`mobile-${l.testid}`} onClick={() => setOpen(false)}
                  className={({ isActive }) => `font-mono text-sm uppercase tracking-widest ${isActive ? "text-court-amber" : "text-zinc-300"}`}>
                  {l.label}
                </NavLink>
              ))}
              {user ? (
                <button data-testid="mobile-nav-logout-btn" onClick={handleLogout} className="text-left font-mono text-sm uppercase tracking-widest text-red-400">Logout ({user.name})</button>
              ) : (
                <button data-testid="mobile-nav-login-btn" onClick={() => { setOpen(false); openAuthModal(); }} className="text-left font-mono text-sm uppercase tracking-widest text-court-amber">Member Login</button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
